import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  consumeTenantQrLoginRequest,
  clearPersistedTenantMobileSession,
  getPostLoginPath,
  hasPersistedTenantMobileSession,
  MOBILE_APP_HOME_PATH,
  persistTenantMobileSession,
  unlockMobileSession,
} from '../../utils/mobileApp';
import {
  clearLastLandlordEmail,
  getBiometricLoginStatus,
  getLastLandlordEmail,
  loginWithBiometrics,
  saveBiometricCredentials,
  saveLastLandlordEmail,
  type BiometricLoginStatus,
} from '../../utils/mobileBiometricLogin';
import kcnBellIcon from '../../assets/icons/kcn-bell.svg';
import MobileTenantQrLogin from './MobileTenantQrLogin';
import { clearToken, getToken, me } from '../../services/authApi';

const MobileLoginPage: React.FC = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [biometric, setBiometric] = useState<BiometricLoginStatus>({
    available: false,
    hasSavedAccount: false,
    label: 'Đăng nhập bằng vân tay',
    icon: 'fa-fingerprint',
    isFace: false,
    hint: '',
  });
  const [biometricBusy, setBiometricBusy] = useState(false);
  const { login, loginWithAccessToken, error, isLoading, user, clearError } = useAuth();
  const [tenantQr, setTenantQr] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const savedLandlordEmail = biometric.savedEmail || getLastLandlordEmail();
  const canUseBiometric = biometric.available && biometric.hasSavedAccount;
  const showSavedEmailBanner = Boolean(savedLandlordEmail) && !tenantQr;

  useEffect(() => {
    const savedEmail = getLastLandlordEmail();
    if (savedEmail) {
      setFormData((prev) => ({ ...prev, email: savedEmail }));
    }
    void getBiometricLoginStatus().then(setBiometric);
    if (consumeTenantQrLoginRequest()) {
      setTenantQr(true);
    }
  }, []);

  const canContinueAsTenant =
    !isLoading &&
    !tenantQr &&
    user?.accountType === 'tenant' &&
    hasPersistedTenantMobileSession();

  const continueAsTenant = () => {
    unlockMobileSession();
    navigate(MOBILE_APP_HOME_PATH, { replace: true });
  };

  const goHome = () => {
    unlockMobileSession();
    const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
    navigate(getPostLoginPath(from || MOBILE_APP_HOME_PATH), { replace: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTenantQr(false);
    clearError();
    try {
      clearPersistedTenantMobileSession();
      clearToken();
      await login(formData.email, formData.password);
      saveLastLandlordEmail(formData.email);
      await saveBiometricCredentials(formData.email, formData.password);
      void getBiometricLoginStatus().then(setBiometric);
      goHome();
    } catch {
      // AuthContext handles error state
    }
  };

  const handleBiometricLogin = async () => {
    if (!canUseBiometric) return;
    setBiometricBusy(true);
    setTenantQr(false);
    clearError();
    try {
      const credentials = await loginWithBiometrics();
      if (!credentials?.email || !credentials.password) return;
      clearPersistedTenantMobileSession();
      clearToken();
      await login(credentials.email, credentials.password);
      saveLastLandlordEmail(credentials.email);
      goHome();
    } catch {
      // AuthContext handles error state
    } finally {
      setBiometricBusy(false);
    }
  };

  const handleChangeAccount = () => {
    clearLastLandlordEmail();
    setFormData({ email: '', password: '' });
    void getBiometricLoginStatus().then(setBiometric);
  };

  useEffect(() => {
    if (tenantQr || isLoading || !canUseBiometric || canContinueAsTenant) {
      return;
    }
    const timer = window.setTimeout(() => {
      void handleBiometricLogin();
    }, 400);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantQr, isLoading, canUseBiometric, canContinueAsTenant]);

  return (
    <div
      className={`flex flex-col bg-[#1b1b1d] px-4 ${
        tenantQr
          ? 'h-[100dvh] max-h-[100dvh] overflow-hidden pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]'
          : 'min-h-screen py-6'
      }`}
    >
      <div
        className={`mx-auto flex w-full max-w-md flex-1 flex-col ${
          tenantQr ? 'min-h-0' : 'justify-center overflow-y-auto'
        }`}
      >
        <div
          className={`rounded-[28px] border border-[#D8B86A] bg-[linear-gradient(145deg,_#FFFDF8_0%,_#FDF7E8_50%,_#F8EFD8_100%)] shadow-[0_24px_60px_rgba(43,37,32,0.28)] ${
            tenantQr ? 'flex min-h-0 flex-1 flex-col overflow-hidden p-4' : 'p-6'
          }`}
        >
          <div className={`shrink-0 text-center ${tenantQr ? 'mb-3' : 'mb-6'}`}>
            <img
              src={kcnBellIcon}
              alt="KCN"
              className={`mx-auto object-contain drop-shadow-md ${
                tenantQr ? 'mb-2 h-14 w-14' : 'mb-4 h-20 w-20'
              }`}
            />
            <h1 className={`font-bold text-[#2B2520] ${tenantQr ? 'text-xl' : 'text-2xl'}`}>
              KCN App
            </h1>
            <p className="mt-2 text-sm text-[#2B2520]/70">
              {tenantQr
                ? 'Khách thuê: nhập số căn cước công dân trên hợp đồng'
                : 'Chủ nhà: đăng nhập email và mật khẩu'}
            </p>
          </div>

          {tenantQr ? (
            <div className="flex min-h-0 flex-1 flex-col">
              <MobileTenantQrLogin
                onSuccess={async () => {
                  const token = getToken();
                  if (!token) throw new Error('Không nhận được phiên đăng nhập');
                  await loginWithAccessToken(token);
                  const profile = await me();
                  persistTenantMobileSession(profile.id);
                  goHome();
                }}
                onCancel={() => {
                  clearError();
                  setTenantQr(false);
                }}
              />
            </div>
          ) : (
            <>
              {canContinueAsTenant && (
                <button
                  type="button"
                  onClick={continueAsTenant}
                  className="mb-4 w-full rounded-xl bg-[#8C5226] py-3 text-sm font-semibold text-white"
                >
                  Tiếp tục với Khách thuê{user?.name ? ` (${user.name})` : ''}
                </button>
              )}

              {showSavedEmailBanner && (
                <div className="mb-4 rounded-xl border border-[#D8B86A]/50 bg-white px-4 py-3">
                  <p className="text-xs text-[#2B2520]/55">Tài khoản chủ nhà</p>
                  <p className="mt-0.5 truncate text-sm font-semibold text-[#2B2520]">{savedLandlordEmail}</p>
                  <button
                    type="button"
                    onClick={handleChangeAccount}
                    className="mt-2 text-xs font-medium text-[#8C5226] underline"
                  >
                    Đổi tài khoản
                  </button>
                </div>
              )}

              {canUseBiometric && (
                <button
                  type="button"
                  onClick={() => void handleBiometricLogin()}
                  disabled={isLoading || biometricBusy}
                  className="mb-4 flex w-full items-center justify-center gap-2 rounded-xl border border-[#C4A35A] bg-white py-3 text-sm font-semibold text-[#8C5226] disabled:opacity-60"
                >
                  <i className={`fas ${biometric.icon} text-lg`} />
                  {biometricBusy ? 'Đang xác thực...' : biometric.label}
                </button>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {!showSavedEmailBanner && (
                  <div>
                    <label htmlFor="mobile-email" className="mb-1 block text-sm font-medium text-[#2B2520]">
                      Email
                    </label>
                    <input
                      id="mobile-email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                      className="w-full rounded-xl border border-[#D8B86A]/40 bg-white px-4 py-3 text-sm text-black outline-none focus:ring-2 focus:ring-[#C4A35A]"
                      placeholder="you@example.com"
                    />
                  </div>
                )}

                <div>
                  <label htmlFor="mobile-password" className="mb-1 block text-sm font-medium text-[#2B2520]">
                    Mật khẩu
                  </label>
                  <div className="relative">
                    <input
                      id="mobile-password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formData.password}
                      onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                      className="w-full rounded-xl border border-[#D8B86A]/40 bg-white px-4 py-3 pr-12 text-sm text-black outline-none focus:ring-2 focus:ring-[#C4A35A]"
                      placeholder="••••••••"
                      autoComplete={canUseBiometric ? 'current-password' : 'password'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((value) => !value)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-black/40"
                      aria-label={showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
                    >
                      <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                    </button>
                  </div>
                </div>

                {error && (
                  <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={isLoading || biometricBusy}
                  className="w-full rounded-xl bg-[#C4A35A] py-3 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {isLoading ? 'Đang đăng nhập...' : 'Đăng nhập'}
                </button>
              </form>

              {biometric.available && biometric.hint && (
                <p className="mt-3 text-center text-xs text-[#2B2520]/55">
                  {biometric.hint}
                </p>
              )}

              <button
                type="button"
                onClick={() => {
                  clearError();
                  setTenantQr(true);
                }}
                className="mt-4 w-full rounded-xl border border-[#C4A35A] bg-white py-3 text-sm font-semibold text-[#8C5226]"
              >
                Khách thuê: đăng nhập bằng CCCD
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileLoginPage;
